<template>
  <section class="hotel-details-panel" :dir="language === 'ar' ? 'rtl' : 'ltr'">
    <div v-if="loading" class="hotel-state hotel-loading" aria-live="polite">
      <div class="state-orb"><b-icon icon="building" /></div>
      <h3>Preparing hotel details</h3>
      <p>We are bringing together property information, amenities and nearby places.</p>
      <div class="skeleton-grid" aria-hidden="true">
        <div class="skeleton-wide" />
        <div v-for="index in 4" :key="index" />
      </div>
    </div>

    <div v-else-if="error" class="hotel-state hotel-error" role="alert">
      <div class="state-orb"><b-icon icon="exclamation-circle" /></div>
      <h3>Hotel details could not be loaded</h3>
      <p>{{ error }}</p>
      <button type="button" @click="fetchHotelDetails">
        <b-icon icon="arrow-clockwise" />
        Try again
      </button>
    </div>

    <div v-else-if="!hotel" class="hotel-state hotel-empty">
      <div class="state-orb"><b-icon icon="building" /></div>
      <h3>No hotel details available</h3>
      <p>{{ emptyMessage || 'Content is not available for this hotel in the selected language.' }}</p>
    </div>

    <div v-else class="hotel-content">
      <header class="hotel-hero">
        <div class="hero-copy">
          <span class="eyebrow">Property intelligence</span>
          <h1>{{ displayValue(hotel.name, 'Hotel details') }}</h1>
          <div class="hero-meta">
            <span v-if="hotel.hotelcode"><b-icon icon="hash" /> {{ hotel.hotelcode }}</span>
            <span v-if="hotel.address"><b-icon icon="geo-alt" /> {{ hotel.address }}</span>
          </div>
        </div>
        <div v-if="hasRating" class="rating-card" aria-label="Hotel rating">
          <strong>{{ hotel.rating }}</strong>
          <div>
            <span>Property rating</span>
            <div class="stars" aria-hidden="true">
              <b-icon v-for="star in ratingStars" :key="star" icon="star-fill" />
            </div>
          </div>
        </div>
      </header>

      <div class="hotel-layout">
        <main class="hotel-main">
          <section v-if="hotel.description" class="detail-card about-card">
            <div class="section-heading">
              <span class="section-icon"><b-icon icon="journal-text" /></span>
              <div><span class="eyebrow">About the property</span><h2>Hotel description</h2></div>
            </div>
            <p>{{ cleanText(hotel.description) }}</p>
          </section>

          <section v-if="images.length" class="detail-card gallery-card">
            <div class="gallery-heading">
              <div class="heading-group">
                <span class="gallery-heading-icon"><b-icon icon="images" /></span>
                <div>
                  <span class="eyebrow">Visual library</span>
                  <h2>Explore the property</h2>
                  <p>Discover every space, detail and experience at a glance.</p>
                </div>
              </div>
              <button type="button" class="gallery-view-all" @click="openGallery()">
                View all photos
                <b-icon icon="arrow-right" />
              </button>
            </div>

            <div class="gallery-preview" :class="`has-${Math.min(previewImages.length, 5)}`">
              <button
                v-for="(image, index) in previewImages"
                :key="`preview-${image.image_url}-${index}`"
                type="button"
                class="preview-tile"
                :class="`preview-tile-${index + 1}`"
                :aria-label="index === previewImages.length - 1 ? `View all ${images.length} hotel photos` : `Open hotel gallery from image ${index + 1}`"
                @click="openGallery(index === previewImages.length - 1 ? 'all' : image.categoryKey)"
              >
                <img :src="image.image_url" :alt="`${hotel.name || 'Hotel'} - ${image.image_type || 'property image'}`" loading="lazy" @error="hideBrokenImage" />
                <span class="preview-scrim" />
                <span v-if="index === previewImages.length - 1" class="preview-count">
                  <b-icon icon="images" />
                  {{ images.length }} photos
                </span>
              </button>
            </div>
          </section>

          <section v-if="roomDetails.length" class="detail-card room-details-card">
            <div class="section-heading split-heading">
              <div class="heading-group">
                <span class="section-icon"><b-icon icon="door-open" /></span>
                <div><span class="eyebrow">Stay essentials</span><h2>Room details</h2></div>
              </div>
              <span class="count-pill">{{ roomDetails.length }} details</span>
            </div>
            <div class="detail-chip-grid">
              <div v-for="(room, index) in roomDetails" :key="`${room}-${index}`" class="detail-chip">
                <b-icon icon="check2" /><span>{{ room }}</span>
              </div>
            </div>
          </section>

        </main>

        <aside class="hotel-sidebar">
          <section v-if="contactItems.length" class="detail-card compact-card">
            <div class="section-heading">
              <span class="section-icon"><b-icon icon="telephone" /></span>
              <div><span class="eyebrow">Get in touch</span><h2>Contact details</h2></div>
            </div>
            <div class="contact-list">
              <div v-for="contact in contactItems" :key="contact.label">
                <b-icon :icon="contact.icon" />
                <span><small>{{ contact.label }}</small><strong>{{ contact.value }}</strong></span>
              </div>
            </div>
          </section>

        </aside>
      </div>

      <section v-if="amenityGroups.length || attractionGroups.length" class="property-explorer">
        <nav class="explorer-tabs" role="tablist" aria-label="Property facilities and nearby places">
          <button
            v-for="tab in explorerTabs"
            :key="tab.key"
            type="button"
            role="tab"
            :aria-selected="activeExplorerTab === tab.key ? 'true' : 'false'"
            :class="{ active: activeExplorerTab === tab.key }"
            @click="selectExplorerTab(tab.key)"
          >
            <b-icon :icon="tab.icon" />
            <span>{{ tab.label }}</span>
            <strong>{{ tab.count }}</strong>
          </button>
        </nav>

        <div class="explorer-body">
          <div v-show="activeExplorerTab === 'amenities'" class="explorer-panel amenities-panel" role="tabpanel">
            <header class="explorer-panel-heading">
              <div><span class="eyebrow">Guest comfort</span><h2>All amenities &amp; services</h2></div>
              <span>{{ amenityCount }} available</span>
            </header>
            <div v-if="amenityGroups.length" class="explorer-amenities">
              <article v-for="(group, index) in amenityGroups" :key="`${group.title}-${index}`">
                <div class="explorer-group-title">
                  <span class="api-icon">
                    <img v-if="group.icon" :src="group.icon" alt="" @error="hideBrokenImage" />
                    <b-icon v-else icon="check2-circle" />
                  </span>
                  <div><h3>{{ group.title }}</h3><small>{{ group.items.length }} services</small></div>
                </div>
                <ul><li v-for="item in group.items" :key="item"><b-icon icon="check-circle-fill" />{{ item }}</li></ul>
              </article>
            </div>
            <div v-else class="explorer-empty"><b-icon icon="inbox" /><span>No amenities available.</span></div>
          </div>

          <div v-show="activeExplorerTab === 'map'" class="explorer-panel map-panel" role="tabpanel">
            <div v-if="hotelCoordinate" class="nearby-map-wrap">
              <l-map
                ref="nearbyMap"
                class="nearby-map"
                :zoom="15"
                :center="mapCenter"
                :options="mapOptions"
              >
                <l-tile-layer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution="&amp;copy; OpenStreetMap contributors"
                />
                <l-circle
                  v-if="mapAttractions.length"
                  :lat-lng="hotelCoordinate"
                  :radius="mapRadius"
                  color="#0097da"
                  :weight="2"
                  :opacity="0.55"
                  fill-color="#2db8c7"
                  :fill-opacity="0.08"
                />
                <l-circle-marker
                  :lat-lng="hotelCoordinate"
                  :radius="13"
                  color="#ffffff"
                  :weight="4"
                  fill-color="#4d5ba4"
                  :fill-opacity="1"
                >
                  <l-popup><div class="map-popup"><strong>{{ hotel.name }}</strong><span>Your hotel</span></div></l-popup>
                </l-circle-marker>
                <l-circle-marker
                  v-for="(place, index) in mapAttractions"
                  :key="`${place.name}-${index}`"
                  :lat-lng="[place.lat, place.lng]"
                  :radius="8"
                  color="#ffffff"
                  :weight="3"
                  fill-color="#e14d4d"
                  :fill-opacity="1"
                >
                  <l-popup>
                    <div class="map-popup"><strong>{{ place.name }}</strong><span>{{ place.category }}<template v-if="hasValue(place.km)"> · {{ formatDistance(place.km) }}</template></span></div>
                  </l-popup>
                </l-circle-marker>
              </l-map>
              <div class="map-legend">
                <span><i class="hotel-dot" /> Hotel</span>
                <span><i class="place-dot" /> Nearby attraction</span>
                <strong>{{ mapAttractions.length }} mapped places</strong>
              </div>
            </div>
            <div v-else class="explorer-empty"><b-icon icon="geo-alt" /><span>Hotel coordinates are not available for the map.</span></div>
          </div>

          <div v-show="activeExplorerTab === 'categories'" class="explorer-panel categories-panel" role="tabpanel">
            <header class="explorer-panel-heading">
              <div><span class="eyebrow">Around the hotel</span><h2>Nearby attractions by category</h2></div>
              <span>{{ attractionCount }} places</span>
            </header>
            <div v-if="attractionGroups.length" class="explorer-categories">
              <article v-for="group in attractionGroups" :key="group.title">
                <div class="explorer-group-title">
                  <span class="category-icon"><img v-if="group.icon" :src="group.icon" alt="" @error="hideBrokenImage" /><b-icon v-else icon="geo-alt" /></span>
                  <div><h3>{{ group.title }}</h3><small>{{ group.places.length }} nearby</small></div>
                </div>
                <ul>
                  <li v-for="(place, index) in group.places" :key="`${place.name}-${index}`">
                    <span><b-icon icon="check-circle-fill" />{{ place.name }}</span>
                    <strong v-if="hasValue(place.km)">{{ formatDistance(place.km) }}</strong>
                  </li>
                </ul>
              </article>
            </div>
            <div v-else class="explorer-empty"><b-icon icon="inbox" /><span>No nearby attractions available.</span></div>
          </div>
        </div>
      </section>
    </div>

    <div
      v-if="galleryOpen"
      class="gallery-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Hotel photo gallery"
      @click.self="closeGallery"
    >
      <section class="gallery-modal-shell">
        <header class="gallery-modal-header">
          <button type="button" class="gallery-back" aria-label="Close photo gallery" @click="closeGallery">
            <b-icon icon="arrow-left" />
          </button>
          <div class="gallery-modal-title">
            <span>Property gallery</span>
            <h2>{{ hotel.name || 'Hotel photos' }}</h2>
          </div>
          <span class="gallery-modal-total"><b-icon icon="images" />{{ images.length }} photos</span>
          <button type="button" class="gallery-close" aria-label="Close photo gallery" @click="closeGallery">
            <b-icon icon="x" />
          </button>
        </header>

        <div class="gallery-modal-body">
          <nav class="gallery-category-cards" role="tablist" aria-label="Photo categories">
            <button
              v-for="category in imageCategories"
              :key="`modal-category-${category.key}`"
              type="button"
              role="tab"
              :aria-selected="activeImageCategory === category.key ? 'true' : 'false'"
              :class="{ active: activeImageCategory === category.key }"
              @click="selectImageCategory(category.key)"
            >
              <img :src="category.thumbnail" alt="" aria-hidden="true" @error="hideBrokenImage" />
              <span class="category-card-shade" />
              <span class="category-card-copy"><strong>{{ category.label }}</strong><small>{{ category.count }}</small></span>
            </button>
          </nav>

          <div class="gallery-results-heading">
            <div><span>{{ activeCategoryLabel }}</span><h3>{{ filteredImages.length }} photos</h3></div>
            <p>Choose any image for a full-screen view</p>
          </div>

          <div class="gallery-modal-grid">
            <figure
              v-for="(image, index) in filteredImages"
              :key="`modal-${image.image_url}-${index}`"
              tabindex="0"
              role="button"
              :aria-label="`Open ${image.categoryLabel} image ${index + 1}`"
              @click="openLightbox(index)"
              @keyup.enter="openLightbox(index)"
            >
              <img :src="image.image_url" :alt="`${hotel.name || 'Hotel'} - ${image.image_type || 'property image'}`" loading="lazy" @error="hideBrokenImage" />
              <span class="modal-image-shade" />
              <figcaption><span>{{ image.categoryLabel }}</span><small>{{ index + 1 }}</small></figcaption>
              <span class="image-expand"><b-icon icon="arrows-fullscreen" /></span>
            </figure>
          </div>
        </div>
      </section>
    </div>

    <div
      v-if="lightboxImage"
      class="gallery-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Hotel image viewer"
      @click.self="closeLightbox"
    >
      <div class="lightbox-topbar">
        <div>
          <span>{{ lightboxImage.categoryLabel }}</span>
          <strong>{{ lightboxIndex + 1 }} of {{ filteredImages.length }}</strong>
        </div>
        <button type="button" aria-label="Close image viewer" @click="closeLightbox">
          <b-icon icon="x" />
        </button>
      </div>
      <button
        v-if="filteredImages.length > 1"
        type="button"
        class="lightbox-arrow previous"
        aria-label="Previous image"
        @click="previousImage"
      >
        <b-icon icon="chevron-left" />
      </button>
      <div class="lightbox-stage">
        <img :src="lightboxImage.image_url" :alt="`${hotel.name || 'Hotel'} - ${lightboxImage.categoryLabel}`" />
      </div>
      <button
        v-if="filteredImages.length > 1"
        type="button"
        class="lightbox-arrow next"
        aria-label="Next image"
        @click="nextImage"
      >
        <b-icon icon="chevron-right" />
      </button>
      <div class="lightbox-caption">
        <span class="lightbox-brand"><b-icon icon="building" /></span>
        <div><strong>{{ hotel.name || 'Hotel gallery' }}</strong><span>{{ lightboxImage.categoryLabel }}</span></div>
      </div>
    </div>
  </section>
</template>

<script>
import axios from 'axios'
import { BIcon } from 'bootstrap-vue'
import {
  LCircle,
  LCircleMarker,
  LMap,
  LPopup,
  LTileLayer,
} from 'vue2-leaflet'
import 'leaflet/dist/leaflet.css'

const HOTEL_DESCRIPTION_API = '/room-mapping-hotel-details/'

export default {
  name: 'HotelDetailsPanel',
  components: {
    BIcon,
    LCircle,
    LCircleMarker,
    LMap,
    LPopup,
    LTileLayer,
  },
  props: {
    supplierCode: { type: String, default: '' },
    supplier: { type: String, default: '' },
    language: { type: String, default: 'en' },
  },
  data() {
    return {
      loading: true,
      error: '',
      emptyMessage: '',
      hotel: null,
      requestId: 0,
      activeImageCategory: 'all',
      galleryOpen: false,
      lightboxIndex: -1,
      bodyOverflow: '',
      activeExplorerTab: 'amenities',
    }
  },
  computed: {
    hasRating() { return this.hasValue(this.hotel && this.hotel.rating) },
    ratingStars() {
      const value = Math.max(0, Math.min(5, Math.round(Number(this.hotel && this.hotel.rating) || 0)))
      return Array.from({ length: value }, (_, index) => index + 1)
    },
    images() {
      const images = this.toArray(this.hotel && this.hotel.images)
      return images
        .filter(image => image && typeof image === 'object' && this.hasValue(image.image_url))
        .map(image => ({
          ...image,
          categoryKey: this.imageCategoryKey(image.image_type),
          categoryLabel: this.imageCategoryLabel(image.image_type),
        }))
    },
    imageCategories() {
      const categories = this.images.reduce((result, image) => {
        if (!result[image.categoryKey]) {
          result[image.categoryKey] = { key: image.categoryKey, label: image.categoryLabel, count: 0 }
        }
        result[image.categoryKey].count += 1
        return result
      }, {})
      return [
        { key: 'all', label: 'All photos', count: this.images.length, thumbnail: this.images[0] && this.images[0].image_url },
        ...Object.values(categories)
          .map(category => ({ ...category, thumbnail: this.images.find(image => image.categoryKey === category.key).image_url }))
          .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label)),
      ]
    },
    previewImages() { return this.images.slice(0, 5) },
    filteredImages() {
      if (this.activeImageCategory === 'all') return this.images
      return this.images.filter(image => image.categoryKey === this.activeImageCategory)
    },
    activeCategoryLabel() {
      const category = this.imageCategories.find(item => item.key === this.activeImageCategory)
      return category ? category.label : 'All photos'
    },
    lightboxImage() {
      return this.lightboxIndex >= 0 ? this.filteredImages[this.lightboxIndex] || null : null
    },
    roomDetails() {
      return this.toArray(this.hotel && this.hotel.room_details)
        .map(item => (typeof item === 'object' ? item.room_detail || item.name || item.value : item))
        .filter(this.hasValue)
    },
    amenityGroups() {
      return this.toArray(this.hotel && this.hotel.amenities).map((group, index) => {
        if (typeof group === 'string') return { title: 'Amenities', icon: '', items: [group] }
        const rawItems = this.toArray(group && (group.list || group.items || group.amenities))
        const items = rawItems.map(item => (typeof item === 'object' ? item.amenity_name || item.name : item)).filter(this.hasValue)
        return {
          title: (group && (group.amenity_name|| group.amenity_alias || group.category)) || `Amenities ${index + 1}`,
          icon: this.iconUrl(group && (group.icon || group.image)),
          items,
        }
      }).filter(group => group.items.length)
    },
    amenityCount() { return this.amenityGroups.reduce((total, group) => total + group.items.length, 0) },
    contactItems() {
      const hotel = this.hotel || {}
      return [
        { label: 'Phone', value: hotel.phone, icon: 'telephone' },
        { label: 'Email', value: hotel.email, icon: 'envelope' },
        { label: 'Fax', value: hotel.fax, icon: 'printer' },
      ].filter(item => this.hasValue(item.value))
    },
    explorerTabs() {
      return [
        { key: 'amenities', label: 'All Amenities', icon: 'grid', count: this.amenityCount },
        { key: 'map', label: 'Nearby Attractions Map', icon: 'map', count: this.mapAttractions.length },
        { key: 'categories', label: 'Attraction Categories', icon: 'collection', count: this.attractionGroups.length },
      ]
    },
    nearbyAttractionLookup() {
      return this.toArray(this.hotel && this.hotel.nearby_attractions).reduce((result, place) => {
        const normalized = this.normalizePlaces([place])[0]
        if (normalized && normalized.name) result[String(normalized.name).trim().toLocaleLowerCase()] = normalized
        return result
      }, {})
    },
    attractionGroups() {
      const grouped = this.hotel && this.hotel.nearby_attractions_category
      if (grouped && typeof grouped === 'object' && !Array.isArray(grouped)) {
        return Object.keys(grouped).map(category => {
          const value = grouped[category]
          const places = this.toArray(value && (value.places || value.items || value))
          const normalizedPlaces = this.normalizePlaces(places).map(place => {
            const match = this.nearbyAttractionLookup[String(place.name || '').trim().toLocaleLowerCase()] || {}
            return {
              ...match,
              ...place,
              lat: this.validCoordinate(place.lat, 90) ? place.lat : match.lat,
              lng: this.validCoordinate(place.lng, 180) ? place.lng : match.lng,
              category: this.titleCase(category),
            }
          })
          return { title: this.titleCase(category), icon: this.iconUrl(value && value.icon), places: normalizedPlaces }
        }).filter(group => group.places.length)
      }
      const flat = this.toArray(this.hotel && this.hotel.nearby_attractions)
      const categories = flat.reduce((result, place) => {
        const category = (place && place.category) || 'Nearby places'
        if (!result[category]) result[category] = []
        result[category].push(place)
        return result
      }, {})
      return Object.keys(categories).map(category => ({
        title: this.titleCase(category),
        icon: '',
        places: this.normalizePlaces(categories[category]).map(place => ({ ...place, category: this.titleCase(category) })),
      }))
    },
    attractionCount() { return this.attractionGroups.reduce((total, group) => total + group.places.length, 0) },
    hotelCoordinate() {
      const latitude = this.hotel && this.hotel.latitude
      const longitude = this.hotel && this.hotel.longitude
      return this.validCoordinate(latitude, 90) && this.validCoordinate(longitude, 180)
        ? [Number(latitude), Number(longitude)]
        : null
    },
    mapAttractions() {
      const seen = new Set()
      return this.attractionGroups.reduce((places, group) => {
        group.places.forEach(place => {
          if (!this.validCoordinate(place.lat, 90) || !this.validCoordinate(place.lng, 180)) return
          const lat = Number(place.lat)
          const lng = Number(place.lng)
          const key = `${place.name}-${lat}-${lng}`
          if (!seen.has(key)) {
            seen.add(key)
            places.push({ ...place, lat, lng, category: place.category || group.title })
          }
        })
        return places
      }, [])
    },
    mapCenter() { return this.hotelCoordinate || [20, 0] },
    mapRadius() {
      const farthestKm = this.mapAttractions.reduce((maximum, place) => {
        const distance = Number(place.km)
        return Number.isFinite(distance) ? Math.max(maximum, distance) : maximum
      }, 0)
      return Math.max(1000, Math.min(Math.ceil(farthestKm * 1000), 15000))
    },
    mapOptions() {
      return { scrollWheelZoom: true, zoomControl: true, attributionControl: true, preferCanvas: true }
    },
  },
  watch: {
    language() { this.fetchHotelDetails() },
    supplierCode(next, previous) { if (next && next !== previous) this.fetchHotelDetails() },
  },
  mounted() {
    this.fetchHotelDetails()
    window.addEventListener('keydown', this.handleGalleryKeydown)
  },
  beforeDestroy() {
    this.requestId += 1
    window.removeEventListener('keydown', this.handleGalleryKeydown)
    this.unlockGalleryScroll()
  },
  methods: {
    async fetchHotelDetails() {
      const currentRequest = this.requestId + 1
      this.requestId = currentRequest
      this.loading = true
      this.error = ''
      this.emptyMessage = ''
      this.hotel = null
      this.resetGallery()
      if (!this.supplierCode) {
        this.loading = false
        this.error = 'Hotel reference is missing. Please return to Client Logs and open the record again.'
        return
      }
      const form = new FormData()
      form.append('SUPPLIER_CODE', this.supplierCode)
      form.append('SUPPLIER', this.supplier || '')
      form.append('LANGUAGE_CODE', this.language || 'en')
      const loginToken = String(localStorage.getItem('token') || '').trim()
      if (!loginToken) {
        this.loading = false
        this.error = 'Your login token is missing. Please sign in again.'
        return
      }
      try {
        const { data } = await axios.post(HOTEL_DESCRIPTION_API, form, {
          headers: {
            Authorization: `Token ${loginToken}`,
          },
        })
        if (currentRequest !== this.requestId) return
        const rows = data && Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : [])
        this.hotel = rows[0] || null
        if (!this.hotel && data && data.message) this.emptyMessage = data.message
        if (this.hotel) this.$nextTick(this.preloadGalleryImages)
      } catch (error) {
        if (currentRequest !== this.requestId) return
        this.error = this.errorMessage(error)
      } finally {
        if (currentRequest === this.requestId) this.loading = false
      }
    },
    imageCategoryKey(value) {
      const normalized = String(value || 'Property').trim().toLocaleLowerCase()
      return normalized.replace(/[^a-z0-9]+/g, '-') || 'property'
    },
    imageCategoryLabel(value) {
      const label = this.titleCase(String(value || 'Property').trim())
      return label || 'Property'
    },
    selectImageCategory(category) {
      this.activeImageCategory = category
      this.closeLightbox()
    },
    openGallery(category = 'all') {
      this.activeImageCategory = this.imageCategories.some(item => item.key === category) ? category : 'all'
      this.galleryOpen = true
      this.lightboxIndex = -1
      this.lockGalleryScroll()
    },
    closeGallery() {
      this.galleryOpen = false
      this.lightboxIndex = -1
      this.unlockGalleryScroll()
    },
    preloadGalleryImages() {
      const priorityUrls = new Set([
        ...this.previewImages.map(image => image.image_url),
        ...this.imageCategories.map(category => category.thumbnail),
      ].filter(Boolean))
      priorityUrls.forEach(url => {
        const preload = new Image()
        preload.src = url
      })
    },
    openLightbox(index) {
      this.lightboxIndex = index
      this.lockGalleryScroll()
    },
    closeLightbox() {
      this.lightboxIndex = -1
      if (!this.galleryOpen) this.unlockGalleryScroll()
    },
    lockGalleryScroll() {
      if (!document.body.classList.contains('hotel-gallery-open')) this.bodyOverflow = document.body.style.overflow
      document.body.classList.add('hotel-gallery-open')
      document.body.style.overflow = 'hidden'
    },
    unlockGalleryScroll() {
      if (!document.body.classList.contains('hotel-gallery-open')) return
      document.body.classList.remove('hotel-gallery-open')
      document.body.style.overflow = this.bodyOverflow
      this.bodyOverflow = ''
    },
    previousImage() {
      if (!this.filteredImages.length) return
      this.lightboxIndex = (this.lightboxIndex - 1 + this.filteredImages.length) % this.filteredImages.length
    },
    nextImage() {
      if (!this.filteredImages.length) return
      this.lightboxIndex = (this.lightboxIndex + 1) % this.filteredImages.length
    },
    handleGalleryKeydown(event) {
      if (this.lightboxImage) {
        if (event.key === 'Escape') this.closeLightbox()
        if (event.key === 'ArrowLeft') this.previousImage()
        if (event.key === 'ArrowRight') this.nextImage()
        return
      }
      if (this.galleryOpen && event.key === 'Escape') this.closeGallery()
    },
    resetGallery() {
      this.activeImageCategory = 'all'
      this.activeExplorerTab = 'amenities'
      this.closeGallery()
    },
    selectExplorerTab(tab) {
      this.activeExplorerTab = tab
      if (tab === 'map') {
        this.$nextTick(() => {
          window.setTimeout(() => {
            const map = this.$refs.nearbyMap && this.$refs.nearbyMap.mapObject
            if (map) {
              map.invalidateSize()
              if (this.hotelCoordinate) map.setView(this.hotelCoordinate, 15, { animate: false })
            }
          }, 80)
        })
      }
    },
    toArray(value) { return Array.isArray(value) ? value : (this.hasValue(value) ? [value] : []) },
    hasValue(value) { return value !== null && value !== undefined && value !== '' },
    displayValue(value, fallback) { return this.hasValue(value) ? value : fallback },
    cleanText(value) {
      const element = document.createElement('div')
      element.innerHTML = String(value || '')
      return (element.textContent || element.innerText || '').replace(/\s+/g, ' ').trim()
    },
    iconUrl(value) { return typeof value === 'string' && /^https?:\/\//i.test(value) ? value : '' },
    normalizePlaces(places) {
      return places.map(place => (typeof place === 'string' ? { name: place, km: null } : {
        name: place && (place.name || place.attraction_name || place.title),
        km: place && (place.km !== undefined ? place.km : place.distance),
        lat: place && (place.lat !== undefined ? place.lat : place.latitude),
        lng: place && (place.long !== undefined ? place.long : (place.lng !== undefined ? place.lng : place.longitude)),
        category: place && place.category,
      })).filter(place => this.hasValue(place.name))
    },
    validCoordinate(value, maximum = 180) {
      if (!this.hasValue(value)) return false
      const numeric = Number(value)
      return Number.isFinite(numeric) && Math.abs(numeric) <= maximum
    },
    formatDistance(value) {
      if (!this.hasValue(value)) return ''
      const text = String(value)
      return /km$/i.test(text) ? text : `${text} km`
    },
    titleCase(value) { return String(value || '').replace(/[_-]+/g, ' ').replace(/\b\w/g, char => char.toUpperCase()) },
    hideBrokenImage(event) { event.target.style.display = 'none' },
    errorMessage(error) {
      const payload = error && error.response && error.response.data
      if (error && error.response && error.response.status === 401) {
        return 'The hotel server did not accept this login token. Your local session is still active.'
      }
      if (payload && payload.message) return payload.message
      if (payload && Array.isArray(payload) && payload[0] && payload[0].Response) return payload[0].Response
      return (error && error.message) || 'Please try again.'
    },
  },
}
</script>

<style scoped>
.hotel-details-panel { --purple: #6d28d9; --purple-dark: #4c1d95; color: #2a2135; text-align: left; }
.hotel-content { display: flex; flex-direction: column; gap: 18px; }
.hotel-hero { min-height: 154px; padding: 28px 30px; border: 1px solid #e7d8f7; border-radius: 18px; display: flex; align-items: center; justify-content: space-between; gap: 24px; overflow: hidden; position: relative; background: radial-gradient(circle at 92% 8%, rgba(217,70,239,.17), transparent 30%), linear-gradient(125deg,#fff 0%,#fcf7ff 56%,#f4e8ff 100%); box-shadow: 0 18px 42px rgba(76,29,149,.08); }
.hotel-hero::after { content: ''; position: absolute; width: 190px; height: 190px; right: -70px; bottom: -120px; border: 1px solid rgba(168,85,247,.25); border-radius: 50%; }
.hero-copy { min-width: 0; position: relative; z-index: 1; }
.eyebrow { display: block; margin-bottom: 5px; color: #8b5cf6; font-size: 10px; font-weight: 900; letter-spacing: .12em; text-transform: uppercase; }
.hero-copy h1 { margin: 0; color: #32174d; font-size: clamp(25px,3vw,38px); line-height: 1.16; }
.hero-meta { margin-top: 12px; display: flex; gap: 14px; flex-wrap: wrap; color: #766582; font-size: 12px; }
.hero-meta span { display: inline-flex; align-items: center; gap: 6px; }
.rating-card { min-width: 170px; padding: 14px; border: 1px solid rgba(109,40,217,.14); border-radius: 15px; display: flex; align-items: center; gap: 12px; position: relative; z-index: 1; background: rgba(255,255,255,.82); backdrop-filter: blur(8px); }
.rating-card > strong { width: 52px; height: 52px; border: 3px solid #c026d3; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; color: var(--purple-dark); font-size: 19px; }
.rating-card span { display: block; margin-bottom: 5px; color: #675672; font-size: 10px; font-weight: 800; }
.stars { display: flex; gap: 3px; color: #f5b800; font-size: 11px; }
.hotel-layout { width: 100%; display: grid; grid-template-columns: minmax(0,2fr) minmax(280px,.72fr); align-items: start; gap: 18px; }
.hotel-main,.hotel-sidebar { display: contents; }
.about-card { min-width: 0; grid-column: 1; grid-row: 1; }
.hotel-sidebar .compact-card { min-width: 0; height: 100%; grid-column: 2; grid-row: 1; align-self: stretch; }
.gallery-card { min-width: 0; grid-column: 1 / -1; grid-row: 2; }
.room-details-card { min-width: 0; grid-column: 1 / -1; grid-row: 3; }
.detail-card { padding: 22px; border: 1px solid #e8dff0; border-radius: 16px; background: rgba(255,255,255,.96); box-shadow: 0 13px 34px rgba(63,34,88,.06); }
.section-heading,.heading-group { display: flex; align-items: center; gap: 11px; }
.split-heading { justify-content: space-between; gap: 16px; }
.section-icon { width: 38px; height: 38px; flex: 0 0 38px; border-radius: 11px; display: inline-flex; align-items: center; justify-content: center; color: var(--purple); background: #f4e9ff; }
.section-heading h2 { margin: 0; color: #2e1e3e; font-size: 18px; font-weight: 850; }
.about-card > p { margin: 18px 0 0; color: #6e6574; font-size: 13px; line-height: 1.8; }
.count-pill { padding: 6px 10px; border-radius: 999px; color: #7e22ce; background: #f6e9ff; font-size: 10px; font-weight: 850; white-space: nowrap; }
.gallery-card { padding: 0; overflow: hidden; border-color: rgba(109,40,217,.13); background: linear-gradient(145deg,#fff 0%,#fdfaff 100%); box-shadow: 0 22px 56px rgba(63,34,88,.1); }
.gallery-heading { padding: 24px 24px 20px; display: flex; align-items: center; justify-content: space-between; gap: 20px; background: radial-gradient(circle at 92% 0%,rgba(192,38,211,.1),transparent 30%),linear-gradient(120deg,#fff,#fbf6ff); }
.gallery-heading-icon { width: 48px; height: 48px; flex: 0 0 48px; border-radius: 14px; display: inline-flex; align-items: center; justify-content: center; color: #fff; background: linear-gradient(145deg,#9333ea,#5b21b6); box-shadow: 0 10px 24px rgba(109,40,217,.24); font-size: 20px; }
.gallery-heading h2 { margin: 0; color: #2e1b3c; font-size: 21px; font-weight: 900; }
.gallery-heading p { margin: 5px 0 0; color: #8b7d92; font-size: 11px; }
.gallery-view-all { min-height: 42px; padding: 0 15px; border: 1px solid #cfe0e9; border-radius: 12px; display: inline-flex; align-items: center; gap: 9px; color: #4d5ba4; background: rgba(255,255,255,.9); font-size: 10px; font-weight: 850; box-shadow: 0 8px 20px rgba(53,75,111,.07); transition: transform .2s ease,box-shadow .2s ease,border-color .2s ease; }
.gallery-view-all:hover { border-color: #2db8c7; transform: translateY(-1px); box-shadow: 0 12px 25px rgba(0,151,218,.13); }
.gallery-preview { height: clamp(330px,38vw,470px); margin: 0 24px 24px; border-radius: 17px; display: grid; grid-template-columns: 2fr 1fr 1fr; grid-template-rows: repeat(2,minmax(0,1fr)); gap: 5px; overflow: hidden; background: #e8eff3; }
.preview-tile { min-width: 0; min-height: 0; padding: 0; border: 0; overflow: hidden; position: relative; cursor: pointer; background: #e8eff3; }
.preview-tile-1 { grid-column: 1; grid-row: 1 / 3; }
.preview-tile-2 { grid-column: 2; grid-row: 1; }
.preview-tile-3 { grid-column: 3; grid-row: 1; }
.preview-tile-4 { grid-column: 2; grid-row: 2; }
.preview-tile-5 { grid-column: 3; grid-row: 2; }
.gallery-preview.has-1 .preview-tile-1 { grid-column: 1 / 4; }
.gallery-preview.has-2 { grid-template-columns: repeat(2,1fr); }
.gallery-preview.has-2 .preview-tile-1 { grid-column: 1; }
.gallery-preview.has-2 .preview-tile-2 { grid-column: 2; grid-row: 1 / 3; }
.gallery-preview.has-3 .preview-tile-2 { grid-column: 2 / 4; }
.gallery-preview.has-3 .preview-tile-3 { grid-column: 2 / 4; grid-row: 2; }
.gallery-preview.has-4 .preview-tile-4 { grid-column: 2 / 4; }
.preview-tile img { width: 100%; height: 100%; display: block; object-fit: cover; transition: transform .55s cubic-bezier(.2,.75,.2,1),filter .35s ease; }
.preview-tile:hover img { transform: scale(1.045); filter: saturate(1.06); }
.preview-tile:focus-visible { z-index: 2; outline: 3px solid #0097da; outline-offset: -3px; }
.preview-scrim { position: absolute; inset: 55% 0 0; background: linear-gradient(transparent,rgba(18,31,55,.48)); pointer-events: none; }
.preview-count { min-height: 40px; padding: 0 13px; border: 1px solid rgba(255,255,255,.5); border-radius: 12px; display: inline-flex; align-items: center; gap: 8px; position: absolute; right: 14px; bottom: 14px; color: #fff; background: rgba(23,38,67,.76); font-size: 11px; font-weight: 850; box-shadow: 0 8px 26px rgba(0,0,0,.2); backdrop-filter: blur(12px); }
.gallery-modal { position: fixed; z-index: 99990; inset: 0; padding: clamp(14px,3vh,34px) clamp(14px,4vw,70px); display: flex; align-items: center; justify-content: center; background: rgba(25,37,61,.68); backdrop-filter: blur(12px); animation: gallery-modal-in .24s ease both; }
.gallery-modal-shell { width: min(1240px,100%); height: min(920px,94vh); border: 1px solid rgba(255,255,255,.68); border-radius: 22px; overflow: hidden; display: flex; flex-direction: column; background: #fff; box-shadow: 0 36px 100px rgba(19,33,60,.34); }
.gallery-modal-header { min-height: 74px; padding: 12px 18px; border-bottom: 1px solid #dce6ee; display: flex; align-items: center; gap: 13px; flex: 0 0 auto; background: rgba(255,255,255,.96); }
.gallery-back,.gallery-close { width: 42px; height: 42px; padding: 0; border: 1px solid #cfdce5; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; color: #4d5ba4; background: #fff; font-size: 17px; transition: color .2s ease,border-color .2s ease,background .2s ease; }
.gallery-back:hover,.gallery-close:hover { border-color: #2db8c7; color: #0097da; background: #eef9fb; }
.gallery-modal-title { min-width: 0; }
.gallery-modal-title > span { display: block; margin-bottom: 2px; color: #0097da; font-size: 8px; font-weight: 850; letter-spacing: .1em; text-transform: uppercase; }
.gallery-modal-title h2 { margin: 0; overflow: hidden; color: #26324d; font-size: 16px; font-weight: 800; text-overflow: ellipsis; white-space: nowrap; }
.gallery-modal-total { min-height: 34px; margin-left: auto; padding: 0 11px; border-radius: 999px; display: inline-flex; align-items: center; gap: 7px; color: #4d5ba4; background: #edf3f7; font-size: 9px; font-weight: 850; white-space: nowrap; }
.gallery-close { margin-left: 2px; }
.gallery-modal-body { min-height: 0; padding: 18px; overflow-y: auto; overscroll-behavior: contain; scrollbar-width: thin; scrollbar-color: #9ebbc9 #edf3f7; }
.gallery-category-cards { padding-bottom: 5px; display: flex; gap: 11px; overflow-x: auto; scrollbar-width: thin; scrollbar-color: #9ebbc9 transparent; }
.gallery-category-cards button { width: 148px; height: 104px; padding: 0; border: 2px solid transparent; border-radius: 16px; overflow: hidden; position: relative; flex: 0 0 148px; color: #fff; background: #dce6ee; box-shadow: 0 8px 20px rgba(38,50,77,.1); transition: transform .2s ease,border-color .2s ease,box-shadow .2s ease; }
.gallery-category-cards button:hover { transform: translateY(-2px); box-shadow: 0 12px 25px rgba(38,50,77,.16); }
.gallery-category-cards button.active { border-color: #0097da; box-shadow: 0 0 0 3px rgba(0,151,218,.13),0 12px 27px rgba(38,50,77,.17); }
.gallery-category-cards img { width: 100%; height: 100%; display: block; object-fit: cover; }
.category-card-shade { position: absolute; inset: 20% 0 0; background: linear-gradient(transparent,rgba(20,33,58,.88)); }
.category-card-copy { position: absolute; right: 11px; bottom: 10px; left: 11px; display: flex; align-items: center; justify-content: space-between; gap: 7px; text-align: left; }
.category-card-copy strong { overflow: hidden; font-size: 10px; font-weight: 850; text-overflow: ellipsis; white-space: nowrap; }
.category-card-copy small { min-width: 24px; height: 24px; padding: 0 5px; border-radius: 999px; display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto; background: rgba(255,255,255,.2); font-size: 8px; backdrop-filter: blur(7px); }
.gallery-results-heading { margin: 23px 1px 14px; display: flex; align-items: flex-end; justify-content: space-between; gap: 15px; }
.gallery-results-heading span { display: block; margin-bottom: 2px; color: #0097da; font-size: 9px; font-weight: 850; text-transform: uppercase; }
.gallery-results-heading h3 { margin: 0; color: #26324d; font-size: 20px; font-weight: 850; }
.gallery-results-heading p { margin: 0; color: #7a8698; font-size: 9px; }
.gallery-modal-grid { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); grid-auto-rows: clamp(220px,28vw,340px); gap: 10px; }
.gallery-modal-grid figure { min-width: 0; min-height: 0; margin: 0; border-radius: 14px; overflow: hidden; position: relative; cursor: zoom-in; background: #e8eff3; isolation: isolate; }
.gallery-modal-grid figure:nth-child(5n + 1) { grid-column: 1 / -1; }
.gallery-modal-grid figure:focus-visible { outline: 3px solid #0097da; outline-offset: -3px; }
.gallery-modal-grid img { width: 100%; height: 100%; display: block; object-fit: cover; transition: transform .5s cubic-bezier(.2,.75,.2,1); }
.gallery-modal-grid figure:hover img { transform: scale(1.04); }
.modal-image-shade { position: absolute; inset: 55% 0 0; z-index: 1; background: linear-gradient(transparent,rgba(19,31,55,.72)); }
.gallery-modal-grid figcaption { position: absolute; z-index: 2; right: 13px; bottom: 12px; left: 13px; display: flex; align-items: center; justify-content: space-between; gap: 8px; color: #fff; }
.gallery-modal-grid figcaption span { overflow: hidden; font-size: 10px; font-weight: 800; text-overflow: ellipsis; white-space: nowrap; }
.gallery-modal-grid figcaption small { width: 25px; height: 25px; border: 1px solid rgba(255,255,255,.35); border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; background: rgba(24,39,68,.48); font-size: 8px; backdrop-filter: blur(8px); }
.image-expand { width: 30px; height: 30px; border: 1px solid rgba(255,255,255,.3); border-radius: 9px; display: inline-flex; align-items: center; justify-content: center; position: absolute; z-index: 2; top: 11px; right: 11px; color: #fff; background: rgba(23,12,31,.35); opacity: 0; transform: translateY(-5px); transition: opacity .25s ease,transform .25s ease; backdrop-filter: blur(8px); }
.gallery-modal-grid figure:hover .image-expand { opacity: 1; transform: translateY(0); }
.gallery-modal-grid figure:nth-child(5n + 1) .image-expand { opacity: 1; transform: none; }
@keyframes gallery-modal-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
.gallery-lightbox { position: fixed; z-index: 99999; inset: 0; display: flex; align-items: center; justify-content: center; color: #fff; background: rgba(13,7,18,.94); backdrop-filter: blur(18px); animation: lightbox-in .22s ease both; }
.lightbox-topbar { position: absolute; z-index: 2; top: 0; right: 0; left: 0; padding: 20px clamp(18px,3vw,42px); display: flex; justify-content: space-between; align-items: center; background: linear-gradient(rgba(8,4,11,.74),transparent); }
.lightbox-topbar > div { display: flex; align-items: center; gap: 10px; }
.lightbox-topbar span { font-size: 12px; font-weight: 800; }
.lightbox-topbar strong { padding: 5px 8px; border-radius: 7px; color: #d8b4fe; background: rgba(255,255,255,.09); font-size: 9px; }
.lightbox-topbar button,.lightbox-arrow { border: 1px solid rgba(255,255,255,.18); display: inline-flex; align-items: center; justify-content: center; color: #fff; background: rgba(255,255,255,.08); backdrop-filter: blur(10px); }
.lightbox-topbar button { width: 42px; height: 42px; border-radius: 12px; font-size: 25px; }
.lightbox-stage { width: min(82vw,1320px); height: min(76vh,850px); display: flex; align-items: center; justify-content: center; }
.lightbox-stage img { max-width: 100%; max-height: 100%; border-radius: 10px; object-fit: contain; box-shadow: 0 30px 90px rgba(0,0,0,.5); }
.lightbox-arrow { width: 48px; height: 58px; border-radius: 14px; position: absolute; z-index: 2; top: 50%; font-size: 20px; transform: translateY(-50%); transition: background .2s ease,transform .2s ease; }
.lightbox-arrow:hover { background: rgba(147,51,234,.55); transform: translateY(-50%) scale(1.04); }
.lightbox-arrow.previous { left: clamp(14px,3vw,45px); }
.lightbox-arrow.next { right: clamp(14px,3vw,45px); }
.lightbox-caption { position: absolute; bottom: 22px; left: 50%; padding: 10px 15px 10px 10px; border: 1px solid rgba(255,255,255,.13); border-radius: 14px; display: flex; align-items: center; gap: 10px; background: rgba(255,255,255,.08); transform: translateX(-50%); backdrop-filter: blur(12px); }
.lightbox-brand { width: 34px; height: 34px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; background: #7e22ce; }
.lightbox-caption strong,.lightbox-caption div > span { display: block; white-space: nowrap; }
.lightbox-caption strong { max-width: 280px; overflow: hidden; font-size: 11px; text-overflow: ellipsis; }
.lightbox-caption div > span { margin-top: 2px; color: #d8cfe0; font-size: 8px; }
@keyframes lightbox-in { from { opacity: 0; } to { opacity: 1; } }
.detail-chip-grid { margin-top: 17px; display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 9px; }
.detail-chip { min-height: 47px; padding: 11px 13px; border: 1px solid #eee5f5; border-radius: 11px; display: flex; align-items: center; gap: 9px; color: #594a63; background: #fdfbff; font-size: 12px; }
.detail-chip .b-icon { color: #9333ea; }
.amenity-groups { margin-top: 18px; display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 12px; }
.amenity-group { padding: 15px; border: 1px solid #ece2f4; border-radius: 13px; background: linear-gradient(145deg,#fff,#fdf9ff); }
.amenity-group-title { display: flex; align-items: center; gap: 10px; }
.api-icon { width: 38px; height: 38px; flex: 0 0 38px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; color: var(--purple); background: #f3e8ff; }
.api-icon img { width: 21px; height: 21px; object-fit: contain; }
.amenity-group h3,.attraction-groups h3 { margin: 0; color: #3d294c; font-size: 13px; font-weight: 850; }
.amenity-group-title div > span { color: #96899d; font-size: 9px; }
.amenity-group ul { margin: 12px 0 0; padding: 0; display: flex; flex-wrap: wrap; gap: 6px; list-style: none; }
.amenity-group li { padding: 5px 8px; border-radius: 7px; color: #695773; background: #f5f0f8; font-size: 10px; }
.compact-card { padding: 19px; }
.contact-list { margin-top: 16px; display: flex; flex-direction: column; gap: 8px; }
.contact-list > div { padding: 11px; border: 1px solid #eee6f3; border-radius: 10px; display: flex; align-items: center; gap: 10px; background: #fdfbff; }
.contact-list > div > .b-icon { color: #9333ea; }
.contact-list small,.contact-list strong { display: block; }
.contact-list small { margin-bottom: 2px; color: #9a8da1; font-size: 9px; text-transform: uppercase; }
.contact-list strong { color: #55415f; font-size: 11px; overflow-wrap: anywhere; }
.attraction-groups { margin-top: 16px; display: flex; flex-direction: column; gap: 13px; }
.attraction-groups article { padding-top: 13px; border-top: 1px solid #eee6f3; }
.attraction-groups article:first-child { padding-top: 0; border-top: 0; }
.attraction-groups h3 { display: flex; align-items: center; gap: 7px; }
.attraction-groups h3 img { width: 17px; height: 17px; }
.attraction-groups ul { margin: 9px 0 0; padding: 0; list-style: none; }
.attraction-groups li { padding: 7px 0; display: flex; justify-content: space-between; gap: 10px; color: #76687d; font-size: 10px; }
.attraction-groups li strong { color: #7e22ce; white-space: nowrap; }
.property-explorer { overflow: hidden; border: 1px solid #e4d7ef; border-radius: 18px; background: #fff; box-shadow: 0 20px 48px rgba(60,31,80,.09); }
.explorer-tabs { min-height: 62px; padding: 7px; display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 6px; background: #f5f0f8; }
.explorer-tabs button { min-height: 48px; padding: 0 14px; border: 1px solid transparent; border-radius: 11px; display: flex; align-items: center; justify-content: center; gap: 8px; color: #74677b; background: transparent; font-size: 11px; font-weight: 850; transition: color .2s ease,background .2s ease,box-shadow .2s ease; }
.explorer-tabs button > .b-icon { font-size: 15px; }
.explorer-tabs button strong { min-width: 24px; height: 24px; padding: 0 5px; border-radius: 999px; display: inline-flex; align-items: center; justify-content: center; color: #7e22ce; background: #eadcf5; font-size: 8px; }
.explorer-tabs button:hover { color: #5b21b6; background: rgba(255,255,255,.65); }
.explorer-tabs button.active { border-color: #dbc5ed; color: #5b21b6; background: #fff; box-shadow: 0 9px 23px rgba(74,39,95,.1); }
.explorer-tabs button.active strong { color: #fff; background: #7e22ce; }
.explorer-body { height: 480px; position: relative; background: linear-gradient(145deg,#fff,#fdfbff); }
.explorer-panel { height: 100%; }
.amenities-panel,.categories-panel { padding: 22px 24px 26px; overflow-y: auto; scrollbar-width: thin; scrollbar-color: #bda5cf #f5f0f8; }
.amenities-panel::-webkit-scrollbar,.categories-panel::-webkit-scrollbar { width: 7px; }
.amenities-panel::-webkit-scrollbar-thumb,.categories-panel::-webkit-scrollbar-thumb { border-radius: 999px; background: #bda5cf; }
.amenities-panel::-webkit-scrollbar-track,.categories-panel::-webkit-scrollbar-track { background: #f5f0f8; }
.explorer-panel-heading { margin-bottom: 21px; padding-bottom: 16px; border-bottom: 1px solid #eee5f4; display: flex; align-items: center; justify-content: space-between; gap: 15px; }
.explorer-panel-heading h2 { margin: 0; color: #33223e; font-size: 18px; font-weight: 900; }
.explorer-panel-heading > span { padding: 7px 10px; border-radius: 999px; color: #6d28d9; background: #f3e8ff; font-size: 9px; font-weight: 850; white-space: nowrap; }
.explorer-amenities,.explorer-categories { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 13px; }
.explorer-amenities article,.explorer-categories article { padding: 16px; border: 1px solid #ece2f3; border-radius: 14px; background: linear-gradient(145deg,#fff,#fcf9ff); box-shadow: 0 8px 20px rgba(65,37,82,.04); }
.explorer-group-title { display: flex; align-items: center; gap: 10px; }
.explorer-group-title h3 { margin: 0; color: #3c2a47; font-size: 12px; font-weight: 900; }
.explorer-group-title small { display: block; margin-top: 2px; color: #9a8ca2; font-size: 8px; }
.explorer-amenities ul,.explorer-categories ul { margin: 13px 0 0; padding: 0; display: flex; flex-direction: column; gap: 8px; list-style: none; }
.explorer-amenities li { display: flex; align-items: flex-start; gap: 7px; color: #75677d; font-size: 10px; line-height: 1.4; }
.explorer-amenities li .b-icon,.explorer-categories li .b-icon { margin-top: 2px; flex: 0 0 auto; color: #c4b7cb; font-size: 9px; }
.category-icon { width: 38px; height: 38px; flex: 0 0 38px; border-radius: 11px; display: inline-flex; align-items: center; justify-content: center; color: #7e22ce; background: #f3e8ff; }
.category-icon img { width: 21px; height: 21px; object-fit: contain; }
.explorer-categories li { padding-bottom: 7px; border-bottom: 1px dashed #eee6f2; display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; color: #75677d; font-size: 10px; }
.explorer-categories li:last-child { padding-bottom: 0; border-bottom: 0; }
.explorer-categories li > span { display: flex; align-items: flex-start; gap: 7px; }
.explorer-categories li strong { flex: 0 0 auto; color: #7e22ce; font-size: 9px; white-space: nowrap; }
.map-panel { overflow: hidden; }
.nearby-map-wrap,.nearby-map { width: 100%; height: 100%; position: relative; }
.nearby-map { z-index: 1; background: #ede8f0; }
.map-legend { position: absolute; z-index: 500; right: 18px; bottom: 18px; left: 18px; min-height: 44px; padding: 8px 12px; border: 1px solid rgba(255,255,255,.65); border-radius: 12px; display: flex; align-items: center; gap: 14px; color: #665a6c; background: rgba(255,255,255,.9); box-shadow: 0 10px 28px rgba(39,25,48,.14); font-size: 9px; font-weight: 800; backdrop-filter: blur(12px); }
.map-legend span { display: inline-flex; align-items: center; gap: 6px; }
.map-legend i { width: 10px; height: 10px; border: 2px solid #fff; border-radius: 50%; display: inline-block; box-shadow: 0 1px 5px rgba(0,0,0,.2); }
.map-legend .hotel-dot { background: #6d28d9; }
.map-legend .place-dot { background: #f43f5e; }
.map-legend strong { margin-left: auto; color: #6d28d9; }
.map-popup { min-width: 150px; padding: 2px; }
.map-popup strong,.map-popup span { display: block; }
.map-popup strong { color: #372441; font-size: 12px; }
.map-popup span { margin-top: 4px; color: #85778c; font-size: 9px; }
.explorer-empty { height: 100%; min-height: 220px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; color: #918498; font-size: 11px; text-align: center; }
.explorer-empty .b-icon { color: #a855f7; font-size: 29px; }
.nearby-map-wrap ::v-deep .leaflet-control-zoom { border: 0; box-shadow: 0 8px 22px rgba(45,28,55,.16); }
.nearby-map-wrap ::v-deep .leaflet-control-zoom a { border: 0; color: #5b21b6; }
.nearby-map-wrap ::v-deep .leaflet-popup-content-wrapper { border-radius: 12px; box-shadow: 0 12px 30px rgba(45,25,57,.18); }
.hotel-state { min-height: 500px; padding: 50px 24px; border: 1px solid #e8ddf1; border-radius: 18px; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; background: rgba(255,255,255,.92); }
.state-orb { width: 62px; height: 62px; margin-bottom: 15px; border-radius: 18px; display: flex; align-items: center; justify-content: center; color: #7e22ce; background: #f3e8ff; font-size: 25px; }
.hotel-state h3 { margin: 0; color: #382344; font-size: 21px; }
.hotel-state p { max-width: 480px; margin: 8px 0 0; color: #887990; font-size: 12px; line-height: 1.6; }
.hotel-state button { margin-top: 18px; min-height: 40px; padding: 0 15px; border: 0; border-radius: 10px; display: inline-flex; align-items: center; gap: 7px; color: #fff; background: linear-gradient(135deg,#7e22ce,#5b21b6); font-size: 12px; font-weight: 800; }
.skeleton-grid { width: min(740px,100%); margin-top: 28px; display: grid; grid-template-columns: repeat(2,1fr); gap: 10px; }
.skeleton-grid div { height: 75px; border-radius: 12px; background: linear-gradient(90deg,#f1e9f7 25%,#fcf9ff 50%,#f1e9f7 75%); background-size: 200% 100%; animation: hotel-shimmer 1.4s infinite; }
.skeleton-grid .skeleton-wide { height: 130px; grid-column: 1/-1; }
@keyframes hotel-shimmer { to { background-position: -200% 0; } }
@media (max-width: 1050px) {
  .hotel-layout { grid-template-columns: 1fr; }
  .about-card { grid-column: 1; grid-row: 1; }
  .hotel-sidebar .compact-card { grid-column: 1; grid-row: 2; }
  .gallery-card { grid-column: 1; grid-row: 3; }
  .room-details-card { grid-column: 1; grid-row: 4; }
  .explorer-amenities,.explorer-categories { grid-template-columns: repeat(2,minmax(0,1fr)); }
}
@media (max-width: 700px) {
  .hotel-hero { padding: 22px 18px; align-items: flex-start; flex-direction: column; }
  .rating-card { width: 100%; }
  .gallery-heading { padding: 19px 17px 16px; align-items: flex-start; }
  .gallery-heading-icon { width: 42px; height: 42px; flex-basis: 42px; }
  .gallery-heading h2 { font-size: 18px; }
  .gallery-heading p { display: none; }
  .gallery-view-all { min-height: 38px; padding: 0 11px; }
  .gallery-view-all .b-icon { display: none; }
  .gallery-preview { height: 330px; margin: 0 17px 17px; grid-template-columns: repeat(4,1fr); grid-template-rows: 2fr 1fr; }
  .preview-tile-1 { grid-column: 1 / 5; grid-row: 1; }
  .preview-tile-2,.preview-tile-3,.preview-tile-4,.preview-tile-5 { grid-row: 2; }
  .preview-tile-2 { grid-column: 1; }
  .preview-tile-3 { grid-column: 2; }
  .preview-tile-4 { grid-column: 3; }
  .preview-tile-5 { grid-column: 4; }
  .gallery-preview.has-1 .preview-tile-1 { grid-column: 1 / 5; grid-row: 1 / 3; }
  .gallery-preview.has-2 .preview-tile-1 { grid-column: 1 / 5; grid-row: 1; }
  .gallery-preview.has-2 .preview-tile-2 { grid-column: 1 / 5; grid-row: 2; }
  .gallery-preview.has-3 .preview-tile-2 { grid-column: 1 / 3; }
  .gallery-preview.has-3 .preview-tile-3 { grid-column: 3 / 5; }
  .gallery-preview.has-4 .preview-tile-2 { grid-column: 1; }
  .gallery-preview.has-4 .preview-tile-3 { grid-column: 2; }
  .gallery-preview.has-4 .preview-tile-4 { grid-column: 3 / 5; }
  .preview-count { right: 10px; bottom: 10px; min-height: 36px; padding: 0 10px; }
  .gallery-modal { padding: 0; }
  .gallery-modal-shell { width: 100%; height: 100%; border: 0; border-radius: 0; }
  .gallery-modal-header { min-height: 66px; padding: 10px 12px; }
  .gallery-modal-title h2 { max-width: 43vw; font-size: 13px; }
  .gallery-modal-total { display: none; }
  .gallery-close { margin-left: auto; }
  .gallery-modal-body { padding: 13px; }
  .gallery-category-cards { gap: 8px; }
  .gallery-category-cards button { width: 116px; height: 86px; flex-basis: 116px; border-radius: 13px; }
  .gallery-results-heading { margin-top: 18px; }
  .gallery-results-heading p { display: none; }
  .gallery-modal-grid { grid-template-columns: 1fr; grid-auto-rows: 245px; gap: 8px; }
  .gallery-modal-grid figure:nth-child(n) { grid-column: 1; }
  .image-expand { opacity: 1; transform: none; }
  .lightbox-stage { width: 100vw; height: 72vh; padding: 0 10px; }
  .lightbox-stage img { border-radius: 7px; }
  .lightbox-arrow { width: 40px; height: 50px; border-radius: 11px; }
  .lightbox-arrow.previous { left: 8px; }
  .lightbox-arrow.next { right: 8px; }
  .lightbox-caption { max-width: calc(100vw - 30px); bottom: 17px; }
  .property-explorer { border-radius: 14px; }
  .explorer-tabs { display: flex; overflow-x: auto; }
  .explorer-tabs button { min-width: 190px; flex: 1 0 190px; }
  .explorer-body { height: 430px; }
  .amenities-panel,.categories-panel { padding: 17px; }
  .explorer-panel-heading { align-items: flex-start; }
  .explorer-panel-heading h2 { font-size: 16px; }
  .explorer-amenities,.explorer-categories { grid-template-columns: 1fr; }
  .map-legend { right: 10px; bottom: 10px; left: 10px; flex-wrap: wrap; gap: 8px 12px; }
  .map-legend strong { width: 100%; margin-left: 0; }
  .amenity-groups,.detail-chip-grid { grid-template-columns: 1fr; }
  .detail-card:not(.gallery-card) { padding: 17px; }
  .split-heading { align-items: flex-start; }
}

/* Bizinso brand system — Brand Guidelines 01.12.2021 */
.hotel-details-panel {
  --brand-cyan: #2db8c7;
  --brand-blue: #0097da;
  --brand-navy: #4d5ba4;
  --brand-gray: #9d9e9e;
  --brand-yellow: #ffd700;
  --brand-orange: #ff8000;
  --brand-red: #e14d4d;
  --brand-ink: #26324d;
  --brand-muted: #6f7a8f;
  --brand-line: #dce6ee;
  color: var(--brand-ink);
  font-family: 'Montserrat', sans-serif;
}
.hotel-content { gap: 20px; }
.hotel-hero {
  min-height: 172px;
  padding: clamp(24px, 3vw, 38px);
  border-color: rgba(0, 151, 218, .2);
  border-radius: 20px;
  background:
    radial-gradient(circle at 88% 8%, rgba(45, 184, 199, .24), transparent 29%),
    linear-gradient(120deg, #fff 0%, #fff 60%, #eef7fb 100%);
  box-shadow: 0 18px 46px rgba(53, 75, 111, .1);
}
.hotel-hero::before {
  content: '';
  width: 5px;
  position: absolute;
  inset: 0 auto 0 0;
  background: linear-gradient(180deg, var(--brand-cyan), var(--brand-blue) 60%, var(--brand-navy));
}
.hotel-hero::after { border-color: rgba(77, 91, 164, .14); }
.eyebrow { color: var(--brand-blue); font-weight: 700; }
.hero-copy h1 { color: var(--brand-ink); font-weight: 600; letter-spacing: -.035em; }
.hero-meta { color: var(--brand-muted); }
.hero-meta .b-icon { color: var(--brand-cyan); }
.rating-card {
  border-color: rgba(77, 91, 164, .18);
  border-radius: 16px;
  background: rgba(255, 255, 255, .9);
  box-shadow: 0 10px 26px rgba(55, 75, 107, .08);
}
.rating-card > strong { border-color: var(--brand-orange); color: var(--brand-navy); }
.rating-card span { color: var(--brand-muted); }
.stars { color: var(--brand-yellow); }
.detail-card {
  border-color: var(--brand-line);
  border-radius: 17px;
  background: rgba(255, 255, 255, .98);
  box-shadow: 0 13px 34px rgba(46, 67, 101, .07);
}
.section-icon,
.api-icon,
.category-icon {
  color: var(--brand-blue);
  background: #eaf8fa;
}
.section-heading h2,
.gallery-heading h2,
.amenity-group h3,
.attraction-groups h3,
.explorer-panel-heading h2,
.explorer-group-title h3 { color: var(--brand-ink); }
.about-card > p,
.gallery-heading p,
.contact-list strong,
.amenity-group li,
.explorer-amenities li,
.explorer-categories li { color: #59667c; }
.count-pill,
.explorer-panel-heading > span {
  color: var(--brand-navy);
  background: #edf2fa;
}
.gallery-card {
  border-color: rgba(0, 151, 218, .2);
  background: linear-gradient(145deg, #fff 0%, #f8fcfd 100%);
  box-shadow: 0 22px 56px rgba(43, 70, 107, .1);
}
.gallery-heading {
  background:
    radial-gradient(circle at 92% 0%, rgba(45, 184, 199, .13), transparent 31%),
    linear-gradient(120deg, #fff 0%, #fff 60%, #eff8fb 100%);
}
.gallery-heading-icon {
  background: linear-gradient(145deg, var(--brand-cyan) 0%, var(--brand-cyan) 60%, var(--brand-blue) 100%);
  box-shadow: 0 10px 24px rgba(0, 151, 218, .23);
}
.gallery-lightbox { background: rgba(27, 38, 68, .95); }
.lightbox-arrow:hover { background: rgba(0, 151, 218, .58); }
.lightbox-brand { background: var(--brand-blue); }
.detail-chip { border-color: #e0e9ee; color: #536177; background: #f9fbfc; }
.detail-chip .b-icon { color: var(--brand-cyan); }
.amenity-group,
.explorer-amenities article,
.explorer-categories article { border-color: #e0e9ee; background: linear-gradient(145deg, #fff, #f8fbfc); }
.contact-list > div { border-color: #e1eaef; background: #f9fbfc; }
.contact-list > div > .b-icon { color: var(--brand-blue); }
.contact-list small { color: #8993a4; }
.attraction-groups li strong,
.explorer-categories li strong { color: var(--brand-blue); }
.property-explorer {
  border-color: #d8e5ec;
  background: #fff;
  box-shadow: 0 20px 48px rgba(44, 66, 100, .09);
}
.explorer-tabs { background: #edf3f7; }
.explorer-tabs button { color: #69758a; }
.explorer-tabs button strong { color: var(--brand-navy); background: #dfe8f2; }
.explorer-tabs button:hover { color: var(--brand-blue); }
.explorer-tabs button.active {
  border-color: #c9dce7;
  color: var(--brand-navy);
  background: #fff;
  box-shadow: 0 9px 23px rgba(48, 71, 108, .1);
}
.explorer-tabs button.active strong { color: #fff; background: var(--brand-blue); }
.explorer-body { background: linear-gradient(145deg, #fff, #f8fbfc); }
.amenities-panel,
.categories-panel { scrollbar-color: var(--brand-cyan) #edf3f7; }
.amenities-panel::-webkit-scrollbar-thumb,
.categories-panel::-webkit-scrollbar-thumb { background: var(--brand-cyan); }
.amenities-panel::-webkit-scrollbar-track,
.categories-panel::-webkit-scrollbar-track { background: #edf3f7; }
.explorer-amenities li .b-icon,
.explorer-categories li .b-icon { color: var(--brand-cyan); }
.nearby-map-wrap ::v-deep .leaflet-control-zoom a { color: var(--brand-navy); }
.map-legend { color: #59667b; }
.map-legend .hotel-dot { background: var(--brand-navy); }
.map-legend .place-dot { background: var(--brand-red); }
.map-legend strong { color: var(--brand-blue); }
.hotel-state { border-color: var(--brand-line); background: rgba(255, 255, 255, .94); }
.state-orb { color: var(--brand-blue); background: #eaf8fa; }
.hotel-state h3 { color: var(--brand-ink); }
.hotel-state p { color: var(--brand-muted); }
.hotel-state button {
  background: linear-gradient(135deg, var(--brand-navy) 0%, var(--brand-navy) 60%, var(--brand-blue) 100%);
}
.skeleton-grid div {
  background: linear-gradient(90deg, #e9f0f4 25%, #f8fbfc 50%, #e9f0f4 75%);
  background-size: 200% 100%;
}
</style>
